// In src/Inquiries.js

import { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot, doc, updateDoc, orderBy, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from './firebase';
import { FaArchive, FaUndo, FaPaperPlane } from 'react-icons/fa';

// This is a new sub-component to handle the conversation/emailing part
function InquiryConversation({ inquiry, onEmailSent }) {
  const [replyMessage, setReplyMessage] = useState('');
  const [isSending, setIsSending] = useState(false);

  const handleSendEmail = async (e) => {
    e.preventDefault();
    if (!replyMessage.trim()) return;
    setIsSending(true);

    try {
      // We use your existing 'mail' collection to trigger the email
      await addDoc(collection(db, "mail"), {
        to: inquiry.email,
        message: {
          subject: `RE: Your Inquiry - ${inquiry.companyName}`,
          html: `
            <p>Hi ${inquiry.companyName},</p>
            <p>${replyMessage.replace(/\n/g, '<br>')}</p>
            <br>
            <p>--- Original Message ---</p>
            <p><strong>From:</strong> ${inquiry.companyName} (${inquiry.email})</p>
            <p><strong>Message:</strong> ${inquiry.message}</p>
          `
        }
      });
      
      // We also save a record of the reply in Firestore
      const conversationRef = collection(db, `advertiser_inquiries/${inquiry.id}/conversation`);
      await addDoc(conversationRef, {
        sender: 'admin',
        message: replyMessage,
        sentAt: serverTimestamp()
      });

      setReplyMessage('');
      onEmailSent(); // Notify parent component to refresh conversation
    } catch (error) {
      console.error("Error sending email:", error);
      alert("Failed to send email. Please check the console.");
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="inquiry-conversation">
      <h4>Conversation History</h4>
      <div className="conversation-thread">
        <div className="conversation-message original">
          <p><strong>{inquiry.companyName} wrote:</strong></p>
          <p>{inquiry.message}</p>
        </div>
        {/* Here you would map over and display saved conversation messages */}
      </div>
      <form onSubmit={handleSendEmail} className="reply-form">
        <textarea
          value={replyMessage}
          onChange={(e) => setReplyMessage(e.target.value)}
          placeholder={`Reply to ${inquiry.email}...`}
          rows="4"
          required
        />
        <button type="submit" className="btn btn-primary" disabled={isSending}>
          <FaPaperPlane />
          {isSending ? 'Sending...' : 'Send Email'}
        </button>
      </form>
    </div>
  );
}


function Inquiries({ isAdmin }) {
  const [activeTab, setActiveTab] = useState('new'); // 'new' or 'archived'
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [processingId, setProcessingId] = useState(null);

  useEffect(() => {
    if (!isAdmin) {
      setLoading(false);
      setError("Verifying admin permissions...");
      return;
    }

    const inquiriesRef = collection(db, "advertiser_inquiries");
    // The query now dynamically listens to the status based on the active tab
    const q = query(inquiriesRef, where("status", "==", activeTab), orderBy("submittedAt", "desc"));
    
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const inquiriesData = [];
      querySnapshot.forEach((doc) => {
        inquiriesData.push({ id: doc.id, ...doc.data() });
      });
      setInquiries(inquiriesData);
      setLoading(false);
      setError('');
    }, (err) => {
      console.error(`Error fetching ${activeTab} inquiries:`, err);
      setError(`Failed to load ${activeTab} inquiries.`);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [isAdmin, activeTab]); // Re-run this effect when the tab changes

  const handleStatusChange = async (inquiryId, newStatus) => {
    const confirmAction = window.confirm(`Are you sure you want to ${newStatus === 'archived' ? 'archive' : 'reopen'} this message?`);
    if (!confirmAction) return;
    
    setProcessingId(inquiryId);
    const inquiryRef = doc(db, "advertiser_inquiries", inquiryId);
    try {
      await updateDoc(inquiryRef, { status: newStatus });
    } catch (err) {
      console.error(`Failed to ${newStatus} inquiry:`, err);
      alert(`Could not update the message. Please try again.`);
    } finally {
      setProcessingId(null);
    }
  };

  if (loading) return <p className="loading-text">Loading inquiries...</p>;
  if (error) return <p className="error-text">{error}</p>;

  return (
    <div className="card">
      <h2 className="page-header">Advertiser Inquiries</h2>
      
      {/* Tab Navigation */}
      <div className="tabs">
        <button 
          className={`tab-btn ${activeTab === 'new' ? 'active' : ''}`}
          onClick={() => setActiveTab('new')}
        >
          New
        </button>
        <button 
          className={`tab-btn ${activeTab === 'archived' ? 'active' : ''}`}
          onClick={() => setActiveTab('archived')}
        >
          Archived
        </button>
      </div>

      <div className="inquiries-list">
        {inquiries.length === 0 ? (
          <p>No {activeTab} inquiries found.</p>
        ) : (
          inquiries.map(inq => (
            <div key={inq.id} className="inquiry-card">
              <div className="inquiry-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <h3>{inq.companyName}</h3>
                  <span className={`status-badge status-${inq.status}`}>{inq.status}</span>
                </div>
                <span className="inquiry-date">{inq.submittedAt?.toDate().toLocaleString()}</span>
              </div>
              
              <InquiryConversation inquiry={inq} onEmailSent={() => { /* future logic to refresh thread */ }} />

              <div className="inquiry-actions">
                {activeTab === 'new' && (
                  <button
                    onClick={() => handleStatusChange(inq.id, 'archived')}
                    disabled={processingId === inq.id}
                    className="btn btn-secondary"
                  >
                    <FaArchive />
                    {processingId === inq.id ? '...' : 'Archive'}
                  </button>
                )}
                {activeTab === 'archived' && (
                  <button
                    onClick={() => handleStatusChange(inq.id, 'new')}
                    disabled={processingId === inq.id}
                    className="btn btn-approve"
                  >
                    <FaUndo />
                    {processingId === inq.id ? '...' : 'Reopen'}
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default Inquiries;